import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button as AriaButton } from 'react-aria-components'
import { DotsVerticalIcon, Edit01Icon, Trash01Icon } from '@untitledui-icons/react/line'

import { Dropdown } from '@/components/base/dropdown/dropdown'
import { formatDate } from '@/lib/dateFormat'
import { cn } from '@/lib/utils'
import type { WidgetTemplate } from '@/hooks/api/useWidgets'
import { WidgetDotBadge } from './WidgetStatusBadge'

/** Matches the date column in the rest of the hub. */
const CARD_DATE_FORMAT = 'dd/MM/yyyy'

interface WidgetTemplateCardProps {
  template: WidgetTemplate
  customerName?: string
  onDelete: () => void
}

export function WidgetTemplateCard({ template, customerName, onDelete }: WidgetTemplateCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const base = 'resources.widgets.templates'

  const editPath = `/widgets/templates/${template.id}`
  const savedAt = template.updated_at ?? template.created_at

  const status = template.assignments.some((assignment) => assignment.website_live)
    ? 'active'
    : template.assignments.length > 0
      ? 'pending'
      : 'disabled'

  return (
    <div className="group relative flex min-h-[124px] flex-col gap-1 rounded-xl border border-gray-200 bg-white p-5 hover:border-gray-400 hover:shadow-xs dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-500">
      <Link
        to={editPath}
        aria-label={template.name}
        className="absolute inset-0 rounded-xl outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-400"
      />

      <div className="flex items-start justify-between gap-3">
        <span className="text-base leading-6 font-medium text-gray-900 dark:text-gray-100">
          {template.name}
        </span>
        {savedAt && (
          <span className="shrink-0 text-xs whitespace-nowrap text-gray-500">
            {formatDate(savedAt, CARD_DATE_FORMAT)}
          </span>
        )}
      </div>

      <span className="text-[13px] leading-5 text-gray-600 dark:text-gray-400">
        {customerName
          ? `${customerName} · ${t(`${base}.assistants_count`, { count: template.assignments.length })}`
          : t(`${base}.assistants_count`, { count: template.assignments.length })}
      </span>

      <div className="relative mt-auto flex flex-wrap items-center gap-1.5">
        <WidgetDotBadge tone={status === 'disabled' ? 'inactive' : status}>
          {t(`resources.widgets.status.${status}`)}
        </WidgetDotBadge>

        {template.kind === 'multi' && (
          <WidgetDotBadge tone="info" withDot={false}>
            {t(`${base}.multi_venue_badge`)}
          </WidgetDotBadge>
        )}

        <Dropdown.Root>
          <AriaButton
            aria-label={t(`${base}.more_actions`, { template: template.name })}
            className={cn(
              'ml-auto flex size-[26px] cursor-pointer items-center justify-center rounded-sm text-gray-500 outline-none',
              'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
              '[&[aria-expanded=true]]:opacity-100',
              'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100'
            )}
          >
            <DotsVerticalIcon className="size-[18px]" />
          </AriaButton>

          <Dropdown.Popover placement="bottom end" className="w-[206px] rounded-md">
            <Dropdown.Menu>
              <Dropdown.Item
                label={t('resources.generic.edit')}
                icon={Edit01Icon}
                onAction={() => navigate(editPath)}
              />
              <Dropdown.Item
                label={t(`${base}.delete_action`)}
                icon={Trash01Icon}
                onAction={onDelete}
                className="[&_span]:text-error-700 [&_svg]:text-error-600"
              />
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      </div>
    </div>
  )
}
